
import { useNavigate } from 'react-router-dom';
import { useOrganizationStore } from '../../lib/stores/organization-store';
import { useUserStore } from '../../lib/stores/user-store';
import { onCurrentOrganizationChanged } from '../../lib/mobile-bridge';
import { useCarriersListStore } from '../../lib/stores/carriers-list-store';
import { useDispatchStore } from '../../lib/stores/dispatch-store';
import { useDocumentsStore } from '../../lib/stores/documents-store';
import { useDriversStore } from '../../lib/stores/drivers-store';
import { useExpensesListStore } from '../../lib/stores/expenses-list-store';
import { useInspectionsStore } from '../../lib/stores/inspections-store';
import { useInvoicesListStore } from '../../lib/stores/invoices-list-store';
import { useLoadsListStore } from '../../lib/stores/loads-list-store';
import { useMaintenanceListStore } from '../../lib/stores/maintenance-list-store';
import { useSettlementsStore } from '../../lib/stores/settlements-store';
import { useTrailersStore } from '../../lib/stores/trailers-store';
import { useTrucksListStore } from '../../lib/stores/trucks-list-store';
import { useWorkOrdersStore } from '../../lib/stores/work-orders-store';
import { useCustomersStore } from '../../lib/stores/customers-store';
import { Badge } from '../ui/badge';
import { routerPaths } from '../../lib/router-paths';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import {
  Users,
  Shield,
  Truck,
  Building2,
  User,
  Calculator,
  ShieldCheck,
  Handshake,
  CheckCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useUIStore } from '../../lib/stores/ui-store';


const roleIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  admin: Shield,
  dispatcher: Truck,
  driver: User,
  accountant: Calculator,
  safety_manager: ShieldCheck,
  broker_agent: Handshake,
  customer_admin: Building2,
  customer_viewer: User,
};

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  dispatcher: 'Dispatcher',
  driver: 'Driver',
  accountant: 'Accountant',
  safety_manager: 'Safety Manager',
  broker_agent: 'Broker Agent',
  customer_admin: 'Portal Admin',
  customer_viewer: 'Portal Viewer',
};

export function OrganizationSwitcher() {
  const navigate = useNavigate();
  const { orgSwitcherOpen: open, setOrgSwitcherOpen: setOpen } = useUIStore();
  const { user } = useUserStore();
  const { organization, organizations, setOrganization } = useOrganizationStore();


  const handleSwitchOrganization = (orgId: string) => {
    const selectedOrg = organizations.find(org => org.id === orgId);
    if (selectedOrg) {
      // Clear all stores except auth and user stores
      useCarriersListStore.getState().resetAll();
      useDispatchStore.getState().resetAll();
      useDocumentsStore.getState().resetAll();
      useDriversStore.getState().resetAll();
      useExpensesListStore.getState().resetAll();
      useInspectionsStore.getState().resetAll();
      useInvoicesListStore.getState().resetAll();
      useLoadsListStore.getState().resetAll();
      useMaintenanceListStore.getState().resetAll();
      useSettlementsStore.getState().resetAll();
      useTrailersStore.getState().resetAll();
      useTrucksListStore.getState().resetAll();
      useWorkOrdersStore.getState().resetAll();
      useCustomersStore.getState().resetAll();

      setOrganization(selectedOrg);

      // Notify mobile bridge about current org change
      onCurrentOrganizationChanged(selectedOrg?.id || null);

      toast.success(`Switched to ${selectedOrg.name}`, {
        description: `You are now working in ${selectedOrg.name}`,
      });

      setOpen(false);
      navigate(routerPaths.dashboard());
    }
  };

  const RoleIcon = roleIcons[user?.role || 'dispatcher'] || User;
  const availableOrganizations = organizations.filter(org => !org.roles.includes('driver'));
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-100 sm:w-135 flex flex-col p-0">
        <SheetHeader className="px-4 pt-6 pb-0">
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Change Organization
          </SheetTitle>
          <SheetDescription>
            Switch to another organization
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 space-y-6 overflow-y-auto flex-1">
          {/* Current User */}
          <div className="p-5 rounded-lg border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-900">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Current User</span>
              <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400">Active</Badge>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/60 flex items-center justify-center">
                <RoleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-emerald-900 dark:text-emerald-100 capitalize">
                  {user?.fullName || (user as any)?.name || 'User'}
                </p>
                <p className="text-sm text-emerald-700/80 dark:text-emerald-400/80 capitalize">
                  {[organization?.roles?.[0]?.replace('_', ' '), organization?.name].filter(Boolean).join(' • ')}
                </p>
              </div>
            </div>
          </div>

          {/* Organization Switcher */}
          {availableOrganizations && availableOrganizations.length > 0 && (
            <>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-amber-600" />
                  <h3 className="font-semibold text-amber-700">Organizations</h3>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    {availableOrganizations.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {availableOrganizations.map((org) => {
                    const isActive = organization?.id === org.id;
                    return (
                      <button
                        key={org.id}
                        onClick={() => handleSwitchOrganization(org.id)}
                        className={cn(
                          'w-full p-4 rounded-lg border text-left transition-all',
                          'hover:bg-amber-50 hover:border-amber-200',
                          isActive && 'bg-amber-50 border-amber-300'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              'h-8 w-8 rounded-full flex items-center justify-center',
                              isActive ? 'bg-amber-200' : 'bg-amber-100'
                            )}>
                              <Building2 className="h-4 w-4 text-amber-700" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{org?.name}</p>
                              <p className="text-xs text-muted-foreground capitalize">
                                {/* {org.businessMode ?? 'Organization'} */}
                                {org.roles?.map((role) => role.replace('_', ' ')).join(', ')}
                              </p>
                            </div>
                          </div>
                          {isActive && (
                            <CheckCircle className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
